/* The climate risk register (EXP-17).
   **No heat map, and no colour by score.** A five-by-five grid coloured red in the corner is the
   product of two ordinals drawn instead of printed, which is the thing RSK-01 refuses — so a card
   is coloured by what needs doing (an overdue review, a rescoring owed), never by how bad the
   score is. */

.stack {
  grid-column: 1 / -1;
  display: grid;
  gap: var(--gap);
  min-width: 0;
}

.tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--gap);
  min-width: 0;
}

/* The register and the form to add to it, side by side; one column below a laptop. */
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 400px);
  gap: var(--gap);
  align-items: start;
  min-width: 0;
}

@media (width <= 1100px) {
  .tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (width <= 560px) {
  .tiles {
    grid-template-columns: minmax(0, 1fr);
  }
}

.asNow {
  margin: 0 0 12px;
  padding: 8px 10px;
  border-left: 3px solid var(--info-line);
  background: var(--info-w);
  border-radius: 0 6px 6px 0;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
  max-width: 90ch;
}

.toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--t-min);
  font-weight: 400;
  color: var(--ink-2);
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.card {
  display: grid;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
}

.cardHead {
  display: flex;
  gap: 8px;
  align-items: baseline;
  flex-wrap: wrap;
}

.title {
  font-size: var(--t-body);
  font-weight: 600;
  color: var(--ink);
}

/* The title is the way into the risk's own page, and says so on hover and focus rather than by
   being coloured like every other link in the card. */
.titleLink {
  color: var(--ink);
  text-decoration: none;
}

.titleLink:hover,
.titleLink:focus-visible {
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.tags {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-left: auto;
}

.tag {
  font-size: var(--t-min);
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--panel-3);
  color: var(--ink-2);
}

.tagQuiet {
  color: var(--muted);
}

/* The score, as the sentence the server sent. Not emphasised by colour: a score is a judgement, and
   making a "4,5" red would be doing with CSS what the arithmetic is not allowed to do. */
.score {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink-2);
}

.body {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

.facts {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  margin-top: 6px;
  font-size: var(--t-min);
  line-height: 1.5;
}

.k {
  color: var(--muted);
}

.v {
  color: var(--ink-2);
}

/* The Next review cell: the date, and under it whatever is owed on the row. Stacked so the cell never
   outgrows its column and runs under its neighbour. */
.reviewCell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.reviewChips {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
}

.overdue {
  color: var(--warn);
  font-weight: 600;
}

/* RSK-02's marker. `--warn`, not `--crit`: a replaced scale is the company improving its own
   process, and red would say somebody had done something wrong. */
.rescore {
  margin: 8px 0 0;
  padding: 8px 10px;
  border: 1px solid var(--warn-line);
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.actions {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: grid;
  gap: 4px;
}

.action {
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: var(--t-min);
}

.actionState {
  font-weight: 700;
  color: var(--warn);
  min-width: 42px;
}

.actionDone {
  color: var(--ok);
}

.actionWhat {
  color: var(--ink-2);
}

.actionWhen {
  color: var(--muted);
  margin-left: auto;
  white-space: nowrap;
}

.rowActions {
  margin: 10px 0 0;
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}

.form {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--line-2);
}

/* A role that cannot record, told so rather than shown a control (UX-04). */
.unauthorized {
  margin: 0;
  padding: 9px 11px;
  border: 1px solid var(--warn-line);
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* The row's own buttons beside the "Act" column's record link. */
.rowButtons {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
